import React, { useCallback, useState } from "react";
import { cilInfo } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import {
  CAlert,
  CButton,
  CCol,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CRow,
  CSpinner,
  CTab,
  CTabContent,
  CTabList,
  CTabPanel,
  CTabs,
  CTooltip,
} from "@coreui/react";
import {
  API_MC_MODES,
  type ApiMcBasis,
  type ApiMcMode,
  type ApiTradeMonteCarloResponse,
} from "@tradingdroid/frontends-types";

import { tdApi } from "@src/services/api/tdApi";
import { getApiErrorMessage } from "@src/utils/apiError";
import { McDistributionTable } from "./McDistributionTable";
import { McFanChart } from "./McFanChart";
import { McRiskSummary } from "./McRiskSummary";

interface Props {
  backtestId: number;
}

// "bootstrap" is the API mode value (the statistics term of art: resampling with replacement). The
// UI says "Resample" instead — "bootstrap" reads as app-startup to most traders, and the house style
// avoids jargon. The wire value sent to the backend stays "bootstrap".
const MODE_LABEL: Record<ApiMcMode, string> = {
  reshuffle: "Reshuffle (same trades, new order)",
  bootstrap: "Resample (with replacement)",
  subsample: "Subsample (keep a fraction)",
};

const MODE_HELP: Record<ApiMcMode, string> = {
  reshuffle:
    "Deals the exact same trades in a new random order. Shows how much of the equity path was the order they happened to arrive in. Terminal return is identical every time.",
  bootstrap:
    "Draws trades at random with replacement, so some repeat and some drop out. Models runs that could have happened, not only reorderings of the ones that did. Terminal return varies.",
  subsample:
    "Keeps a random fraction of the trades each run. Tests whether the result leans on a handful of trades or holds up on a smaller sample.",
};

const InfoHint: React.FC<{ help: string }> = ({ help }) => (
  <CTooltip content={help} placement="top">
    <CIcon className="ms-1 text-info" icon={cilInfo} size="sm" style={{ cursor: "help" }} />
  </CTooltip>
);

const BASIS_LABEL: Record<ApiMcBasis, string> = {
  quote_additive: "Quote additive (canonical)",
  pct_compound: "Percent compound",
};

/**
 * Trade-level Monte Carlo for a completed backtest.
 *
 * Nothing runs until the operator presses Run: the endpoint is synchronous and the cost grows with
 * the simulation count, so an automatic fetch on tab open would charge every visit.
 */
export const TradeMonteCarloPanel: React.FC<Props> = ({ backtestId }) => {
  const [modes, setModes] = useState<ApiMcMode[]>(["reshuffle", "bootstrap"]);
  const [nSims, setNSims] = useState(2000);
  const [basis, setBasis] = useState<ApiMcBasis>("quote_additive");
  // One number does double duty: it is the drawdown the operator is willing to stomach. It goes to
  // the backend as the ruin threshold (so risk-of-ruin becomes "chance of hitting my limit") and it
  // is the reference every drawdown traffic light is colored against.
  const [drawdownLimitPct, setDrawdownLimitPct] = useState(25);
  const [includeFanChart, setIncludeFanChart] = useState(true);

  const [result, setResult] = useState<ApiTradeMonteCarloResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleMode = (mode: ApiMcMode) =>
    setModes((current) =>
      current.includes(mode) ? current.filter((m) => m !== mode) : [...current, mode],
    );

  // A cleared or non-integer number input reads back as NaN, which would reach the API as an invalid
  // body. Bound both fields to the same ranges the server enforces so Run stays disabled until valid.
  const nSimsValid = Number.isInteger(nSims) && nSims >= 100 && nSims <= 5000;
  const limitValid =
    Number.isFinite(drawdownLimitPct) && drawdownLimitPct > 0 && drawdownLimitPct <= 100;
  const canRun = !running && modes.length > 0 && nSimsValid && limitValid;

  const run = useCallback(async () => {
    setRunning(true);
    setError(null);
    // Drop the previous run's numbers up front so a failed or in-flight rerun can never show results
    // that no longer match the form.
    setResult(null);
    try {
      const response = await tdApi.monteCarlo(backtestId, {
        modes,
        n_sims: nSims,
        basis,
        ruin_drawdown_pct: drawdownLimitPct,
        include_fan_chart: includeFanChart,
      });
      setResult(response ?? null);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setRunning(false);
    }
  }, [backtestId, modes, nSims, basis, drawdownLimitPct, includeFanChart]);

  const modeNames = result ? Object.keys(result.modes) : [];

  return (
    <div className="TradeMonteCarloPanel">
      <CRow className="TradeMonteCarloForm align-items-end g-3 mb-3">
        <CCol md={4}>
          <CFormLabel className="mb-1">Modes</CFormLabel>
          <div className="TradeMonteCarloModes">
            {API_MC_MODES.map((mode) => (
              <CFormCheck
                key={mode}
                id={`mc-mode-${mode}`}
                label={MODE_LABEL[mode]}
                checked={modes.includes(mode)}
                onChange={() => toggleMode(mode)}
              />
            ))}
          </div>
        </CCol>

        <CCol md={2}>
          <CFormLabel htmlFor="mc-n-sims" className="mb-1">
            Simulations
          </CFormLabel>
          <CFormInput
            id="mc-n-sims"
            type="number"
            min={100}
            max={5000}
            step={100}
            value={nSims}
            onChange={(e) => setNSims(Number(e.target.value))}
          />
        </CCol>

        <CCol md={2}>
          <CFormLabel htmlFor="mc-limit" className="mb-1">
            Max Acceptable Drawdown (%)
          </CFormLabel>
          <CFormInput
            id="mc-limit"
            type="number"
            min={1}
            max={100}
            step={5}
            value={drawdownLimitPct}
            onChange={(e) => setDrawdownLimitPct(Number(e.target.value))}
          />
        </CCol>

        <CCol md={2}>
          <CFormLabel htmlFor="mc-basis" className="mb-1">
            Basis
          </CFormLabel>
          <CFormSelect
            id="mc-basis"
            value={basis}
            onChange={(e) => setBasis(e.target.value as ApiMcBasis)}
          >
            {(Object.keys(BASIS_LABEL) as ApiMcBasis[]).map((key) => (
              <option key={key} value={key}>
                {BASIS_LABEL[key]}
              </option>
            ))}
          </CFormSelect>
        </CCol>

        <CCol md={2} className="d-flex flex-column gap-2">
          <CFormCheck
            id="mc-fan-chart"
            label="Fan chart"
            checked={includeFanChart}
            onChange={(e) => setIncludeFanChart(e.target.checked)}
          />
          <CButton color="primary" onClick={run} disabled={!canRun}>
            {running ? <CSpinner size="sm" /> : "Run"}
          </CButton>
        </CCol>
      </CRow>

      {error && <CAlert color="warning">Monte Carlo failed: {error}</CAlert>}

      {result?.status === "insufficient_data" && (
        <CAlert color="info">
          This backtest has too few closed trades to resample ({result.source.closed_trade_count}).
        </CAlert>
      )}

      {result?.warnings.map((warning) => (
        <CAlert key={warning} color="warning">
          {warning}
        </CAlert>
      ))}

      {result && modeNames.length > 0 && (
        <>
          <div className="TradeMonteCarloSource text-body-secondary mb-2">
            {result.source.closed_trade_count} closed trades, ${result.source.initial_capital}{" "}
            starting capital, {result.source.basis} basis
            {result.cache.hit && ", served from cache"}
            {result.source.ignored_open_positions > 0 &&
              `, ${result.source.ignored_open_positions} open positions ignored`}
          </div>

          {(() => {
            // Color everything against the limit this result was actually run with, not the live
            // form value (which may have moved since Run without a re-run).
            const appliedLimit = Number(result.request.ruin_drawdown_pct) || drawdownLimitPct;
            return (
              <CTabs key={modeNames.join(",")} defaultActiveItemKey={modeNames[0]}>
                <CTabList variant="underline-border" className="mb-2">
                  {modeNames.map((name) => (
                    <CTab key={name} itemKey={name} className="text-capitalize">
                      {name}
                      {name in MODE_HELP && <InfoHint help={MODE_HELP[name as ApiMcMode]} />}
                    </CTab>
                  ))}
                </CTabList>
                <CTabContent>
                  {modeNames.map((name) => {
                    const mode = result.modes[name];
                    return (
                      <CTabPanel
                        key={name}
                        itemKey={name}
                        className="TradeMonteCarloModePanel pt-2"
                      >
                        <McRiskSummary
                          risk={mode.risk}
                          limitPct={appliedLimit}
                          terminalInvariant={
                            mode.invariant_metrics.includes("terminal_equity") ||
                            mode.invariant_metrics.includes("total_return_pct")
                          }
                        />
                        <McDistributionTable
                          mode={mode}
                          original={result.source.original}
                          limitPct={appliedLimit}
                        />
                        {mode.fan_chart && (
                          <McFanChart
                            fanChart={mode.fan_chart}
                            initialCapital={result.source.initial_capital}
                          />
                        )}
                      </CTabPanel>
                    );
                  })}
                </CTabContent>
              </CTabs>
            );
          })()}
        </>
      )}

      {/* Holds the block at roughly the height of the populated tabs, so opening the Monte Carlo tab
          or waiting on a run does not collapse the card and jolt the layout. */}
      {!result && !error && (
        <div className="TradeMonteCarloPlaceholder d-flex align-items-center justify-content-center text-body-secondary">
          {running ? (
            <span className="d-flex align-items-center gap-2">
              <CSpinner size="sm" /> Running the simulation...
            </span>
          ) : (
            "Run the simulation to see the distributions, risk of ruin and the equity fan chart."
          )}
        </div>
      )}
    </div>
  );
};
